import {
  type DefaultError,
  type MutationFunctionContext,
  type QueryClient,
  type UseMutationOptions,
  useMutation,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useState } from "react";

import { type ValidationError } from "../types";
import { isValidationError } from "../utils";

// : UseMutationResult<TData, TError, TVariables, TContext>
export function useMutationWithError<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown,
>(
  {
    mutationFn,
    ...options
  }: UseMutationOptions<TData, TError, TVariables, TContext>,
  queryClient?: QueryClient,
) {
  const [errors, setErrors] = useState<ValidationError>();

  const errorCheck = (error: unknown) => {
    if (
      isAxiosError(error) &&
      error.response?.status === 422 &&
      isValidationError(error.response.data)
    ) {
      setErrors(error.response.data);
    }

    return Promise.reject(error);
  };

  return {
    ...useMutation(
      {
        ...options,
        mutationFn: mutationFn
          ? (data: TVariables, context: MutationFunctionContext) =>
              mutationFn(data, context).catch(errorCheck)
          : undefined,
      },
      queryClient,
    ),
    errors,
    reset: () => setErrors(undefined),
  };
}
